import { TrashIcon } from '@heroicons/react/24/outline'
import Combo from 'app/components/ui/combo/combo'
import { Button } from 'app/components/ui/button'
import FloatingSave from 'app/components/FloatingSave'
import { SectionCard } from 'app/components/SectionCard'
import type { UseAvailabilitySettingsReturn } from './useAvailabilitySettings'
import {
  availabilityModeOptions,
  booleanOptions,
  doubleBookingCountOptions,
  getAvailabilityModeOption,
  getBooleanOption,
  getDoubleBookingCountOption,
  statusOptions,
  timeIncrementOptions,
} from './useAvailabilitySettings'

export type AvailabilitySettingsComponentProps = UseAvailabilitySettingsReturn

export function AvailabilitySettingsComponent(props: AvailabilitySettingsComponentProps) {
  const {
    schedulingConfiguration,
    setSchedulingConfiguration,
    locationSettings,
    setLocationSettings,
    isDirtyForm,
    setIsDirtyForm,
    sortField,
    sortDirection,
    hasScheduler,
    hasAgent,
    isExternalSlots,
    isKolla,
    sortedInsuranceRows,
    handleInsuranceStatusChange,
    handleResetInsurance,
    handleSaveConfiguration,
    headerButtons,
    toggleSort,
  } = props

  return (
    <>
      <div className="mt-4 flex flex-col gap-6 pb-32">
        <SectionCard title="Enabled" className="max-w-187.5">
          {hasScheduler && (
            <div className="flex flex-col gap-1.5">
              <label className="text-label-sm text-default">Scheduler Live</label>
              <Combo
                name="isSchedulerLive"
                options={booleanOptions}
                value={getBooleanOption(locationSettings.isSchedulerLive)}
                onChange={(option) => {
                  setLocationSettings({ ...locationSettings, isSchedulerLive: option.value === `true` })
                  setIsDirtyForm(true)
                }}
                className="bg-surface"
              />
            </div>
          )}
          {hasAgent && (
            <div className="flex flex-col gap-1.5">
              <label className="text-label-sm text-default">AI Agent Live</label>
              <Combo
                name="isAIAgentLive"
                options={booleanOptions}
                value={getBooleanOption(locationSettings.isAIAgentLive)}
                onChange={(option) => {
                  setLocationSettings({ ...locationSettings, isAIAgentLive: option.value === `true` })
                  setIsDirtyForm(true)
                }}
                className="bg-surface"
              />
            </div>
          )}
        </SectionCard>

        {isKolla && (
          <SectionCard title="Availability Mode" className="max-w-187.5">
            {/* Standard mode derives bookable hours, operatories and provider assignments from
                the Kolla PMS; Advanced mode uses availabilities configured manually in Trackable. */}
            <div className="flex flex-col gap-1.5">
              <label className="text-label-sm text-default">Availability Mode</label>
              <Combo
                name="availabilityMode"
                options={availabilityModeOptions}
                value={getAvailabilityModeOption(locationSettings.availabilityMode)}
                onChange={(option) => {
                  setLocationSettings({ ...locationSettings, availabilityMode: option.value })
                  setIsDirtyForm(true)
                }}
                className="bg-surface"
              />
            </div>
          </SectionCard>
        )}

        <SectionCard title="General Settings" className="max-w-187.5">
          {/* Time Increments and Double Booking are controlled by the external PMS for
              CareStack/Ascend locations, so hide them there. */}
          {!isExternalSlots && (
            <div className="flex flex-col gap-1.5">
              <label className="text-label-sm text-default">Allow Double Booking</label>
              <Combo
                name="allowDoubleBooking"
                options={booleanOptions}
                value={getBooleanOption(!!schedulingConfiguration?.allowDoubleBooking)}
                onChange={(option) => {
                  const value = option.value === `true`
                  setSchedulingConfiguration({
                    ...schedulingConfiguration,
                    allowDoubleBooking: value,
                  })
                  setIsDirtyForm(true)
                }}
                className="bg-surface"
              />
            </div>
          )}
          {!isExternalSlots && schedulingConfiguration?.allowDoubleBooking && (
            <div className="flex flex-col gap-1.5">
              <label className="text-label-sm text-default">Max Concurrent Bookings</label>
              <Combo
                name="doubleBookingCount"
                options={doubleBookingCountOptions}
                value={getDoubleBookingCountOption(schedulingConfiguration?.doubleBookingCount)}
                onChange={(option) => {
                  setSchedulingConfiguration({
                    ...schedulingConfiguration,
                    doubleBookingCount: Number(option.value),
                  })
                  setIsDirtyForm(true)
                }}
                className="bg-surface"
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <label className="text-label-sm text-default">Accept State / HMO Insurances</label>
            <Combo
              name="isStateInsuranceAccepted"
              options={booleanOptions}
              value={getBooleanOption(!!schedulingConfiguration?.isStateInsuranceAccepted)}
              onChange={(option) => {
                const value = option.value === `true`
                setSchedulingConfiguration({
                  ...schedulingConfiguration,
                  isStateInsuranceAccepted: value,
                })
                setIsDirtyForm(true)
              }}
              className="bg-surface"
            />
          </div>
          {!isExternalSlots && (
            <div className="flex flex-col gap-1.5">
              <label className="text-label-sm text-default">Time Increments</label>
              <Combo
                name="granularity"
                options={timeIncrementOptions}
                value={
                  timeIncrementOptions.find((o) => o.value === String(schedulingConfiguration?.granularity ?? 60)) ??
                  timeIncrementOptions[3]
                }
                onChange={(option) => {
                  setSchedulingConfiguration({
                    ...schedulingConfiguration,
                    granularity: Number(option.value),
                  })
                  setIsDirtyForm(true)
                }}
                className="bg-surface"
              />
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Insurances"
          className="max-w-187.5"
          contentClassName="gap-0 overflow-hidden p-0"
          headerButtons={headerButtons}
        >
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-default bg-surface-subtle">
                  <th
                    className="cursor-pointer select-none px-4 py-4 text-left text-label-sm text-default hover:text-strong"
                    onClick={() => toggleSort(`name`)}
                  >
                    Insurance Name {sortField === `name` ? (sortDirection === `asc` ? `↑` : `↓`) : ``}
                  </th>
                  <th
                    className="cursor-pointer select-none border-l border-default px-4 py-4 text-left text-label-sm text-default hover:text-strong"
                    onClick={() => toggleSort(`status`)}
                  >
                    Status {sortField === `status` ? (sortDirection === `asc` ? `↑` : `↓`) : ``}
                  </th>
                  <th className="w-15 border-l border-default px-4 py-4 text-center text-label-sm text-default">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedInsuranceRows.map((row) => {
                  const currentOption = statusOptions.find((o) => o.id === row.status) ?? statusOptions[0]
                  return (
                    <tr key={row.name} className="group/row border-b border-default last:border-b-0">
                      <td className="px-4 py-4 text-label-sm text-strong">{row.name}</td>
                      <td className="border-l border-default px-4 py-2">
                        <div className="group/cell">
                          <Combo
                            name="status"
                            options={statusOptions.filter((o) => o.id !== `Not Set`)}
                            value={currentOption}
                            onChange={(option) =>
                              handleInsuranceStatusChange(row.name, option.value as `Accepted` | `Rejected` | `Not Set`)
                            }
                            inputClassName="!h-full !border-0 !shadow-none !py-0 !pl-0 !pr-5 cursor-pointer text-body-xs"
                            chevronClassName="hidden group-hover/cell:block"
                          />
                        </div>
                      </td>
                      <td className="border-l border-default px-4 py-2 text-center">
                        <Button
                          variant="subtle"
                          size="icon-sm"
                          onClick={() => handleResetInsurance(row.name)}
                          title="Remove insurance"
                          aria-label={`Remove ${row.name}`}
                        >
                          <TrashIcon className="size-5" />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {sortedInsuranceRows.length === 0 && (
              <div className="px-4 py-8 text-center text-ui-sm text-minimal">No insurances configured</div>
            )}
          </>
        </SectionCard>
      </div>
      <FloatingSave isDirtyForm={isDirtyForm} handleSaveConfiguration={handleSaveConfiguration} />
    </>
  )
}
